import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import LinkIcon from "@mui/icons-material/Link";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../../api";
import { adminHasPermission } from "../../utils/adminPermissions";
import { sanitizeStripeCheckoutUrl } from "../../utils/stripeCheckoutUrl";

const formatMoney = (pence, currency = "gbp") => {
  const amount = Number(pence || 0) / 100;
  try {
    return new Intl.NumberFormat("en-GB", {
      style: "currency",
      currency: (currency || "gbp").toUpperCase(),
    }).format(amount);
  } catch {
    return `£${amount.toFixed(2)}`;
  }
};

const formatDate = (value) => {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return value;
  }
};

const statusColor = (status) => {
  switch (status) {
    case "active":
    case "completed":
    case "paid":
      return "success";
    case "trial":
      return "info";
    case "trial_ended_pending":
    case "pending":
      return "warning";
    default:
      return "default";
  }
};

const MobileUserDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const canUpdate = adminHasPermission("update-mobile-users") || adminHasPermission("update-parents");
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [billingOpen, setBillingOpen] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [submittingLink, setSubmittingLink] = useState(false);
  const [submittingOffline, setSubmittingOffline] = useState(false);
  const [createdLinkUrl, setCreatedLinkUrl] = useState(null);
  const [createdLinkEmailInfo, setCreatedLinkEmailInfo] = useState(null);
  const [sendEmail, setSendEmail] = useState(true);
  const [receiptReference, setReceiptReference] = useState("");
  const [amount, setAmount] = useState("");
  const [adminNotes, setAdminNotes] = useState("");
  const [bankReceiptFile, setBankReceiptFile] = useState(null);
  const bankReceiptInputRef = useRef(null);

  const fetchDetail = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get(`admin/mobile-users/${id}`);
      if (res.data?.success) {
        setDetail(res.data.data);
        const plans = res.data.data?.available_plans || [];
        const standard = plans.find((p) => p.slug === "standard-monthly");
        setSelectedPlanId(String(standard?.id || plans[0]?.id || ""));
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to load mobile user.");
      navigate("/admin/mobile-users");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  const resetBillingForm = () => {
    setCreatedLinkUrl(null);
    setCreatedLinkEmailInfo(null);
    setSendEmail(true);
    setReceiptReference("");
    setAmount("");
    setAdminNotes("");
    setBankReceiptFile(null);
    if (bankReceiptInputRef.current) bankReceiptInputRef.current.value = "";
  };

  const handleCreatePaymentLink = async () => {
    if (!canUpdate) return;
    setSubmittingLink(true);
    try {
      const res = await api.post(`admin/mobile-users/${id}/payment-link`, {
        plan_id: selectedPlanId ? Number(selectedPlanId) : undefined,
        send_email: sendEmail,
      });
      const url = sanitizeStripeCheckoutUrl(res.data?.data?.checkout_url);
      if (!url) throw new Error(res.data?.message || "No checkout URL returned.");
      setCreatedLinkUrl(url);
      setCreatedLinkEmailInfo({
        sent: res.data?.data?.email_sent === true,
        email: res.data?.data?.recipient_email || null,
        error: res.data?.data?.email_error || null,
      });
      toast.success(res.data?.message || "Payment link created.");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Could not create payment link.");
    } finally {
      setSubmittingLink(false);
    }
  };

  const handleOfflinePayment = async () => {
    if (!canUpdate || !bankReceiptFile) {
      toast.error("Upload a bank transfer receipt (jpg, png, or pdf).");
      return;
    }
    setSubmittingOffline(true);
    try {
      const form = new FormData();
      form.append("receipt", bankReceiptFile);
      if (selectedPlanId) form.append("plan_id", selectedPlanId);
      if (receiptReference.trim()) form.append("receipt_reference", receiptReference.trim());
      if (adminNotes.trim()) form.append("admin_notes", adminNotes.trim());
      if (amount.trim()) form.append("amount", amount.trim());

      const res = await api.post(`admin/mobile-users/${id}/offline-payment`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data?.message || "Subscription activated.");
      setBillingOpen(false);
      resetBillingForm();
      await fetchDetail();
    } catch (e) {
      toast.error(e?.response?.data?.message || "Could not record offline payment.");
    } finally {
      setSubmittingOffline(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!detail) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <Typography color="error">Mobile user not found.</Typography>
      </Box>
    );
  }

  const user = detail.user || {};
  const access = detail.access || {};
  const current = detail.current_subscription;
  const plans = detail.available_plans || [];

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Button startIcon={<ArrowBackIcon />} onClick={() => navigate("/admin/mobile-users")} sx={{ mb: 2 }}>
        Back to mobile users
      </Button>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: "column", md: "row" }} justifyContent="space-between" spacing={2}>
            <Box>
              <Typography variant="h5" fontWeight={700}>
                {user.full_name || "Mobile user"}
              </Typography>
              <Typography color="text.secondary">{user.email}</Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1, flexWrap: "wrap" }}>
                <Chip size="small" label={`ID ${user.id}`} />
                {user.pet_name ? <Chip size="small" label={`Pet: ${user.pet_name}`} /> : null}
                {access.is_tester_account ? <Chip size="small" color="warning" label="Tester account" /> : null}
                <Chip
                  size="small"
                  color={access.has_resource_access ? "success" : "default"}
                  label={access.has_resource_access ? "App access" : "Payment required"}
                />
              </Stack>
            </Box>
            {canUpdate ? (
              <Button variant="contained" startIcon={<PaymentsIcon />} onClick={() => setBillingOpen(true)}>
                Manage billing
              </Button>
            ) : null}
          </Stack>
        </CardContent>
      </Card>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Profile
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              <Typography variant="body2"><strong>Email verified:</strong> {formatDate(user.email_verified_at)}</Typography>
              <Typography variant="body2"><strong>Joined:</strong> {formatDate(user.created_at)}</Typography>
              <Typography variant="body2"><strong>Platform:</strong> {user.platform || "—"}</Typography>
              <Typography variant="body2"><strong>Country:</strong> {user.billing_country || "—"}</Typography>
              <Typography variant="body2"><strong>Last seen:</strong> {formatDate(user.last_seen_at)}</Typography>
              <Typography variant="body2">
                <strong>Card on file:</strong>{" "}
                {user.card_on_file ? `${user.card_brand || "card"} •••• ${user.card_last4}` : "None"}
              </Typography>
              <Typography variant="body2"><strong>Free trial used:</strong> {access.free_trial_consumed ? "Yes" : "No"}</Typography>
              <Typography variant="body2"><strong>Can start free trial:</strong> {access.can_start_free_trial ? "Yes" : "No"}</Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Current subscription
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              <Typography variant="body2"><strong>Display plan:</strong> {detail.subscription_display_name || "—"}</Typography>
              <Typography variant="body2">
                <strong>Status:</strong>{" "}
                {current ? (
                  <Chip size="small" label={current.status} color={statusColor(current.status)} />
                ) : (
                  "No active subscription"
                )}
              </Typography>
              <Typography variant="body2"><strong>Provider:</strong> {current?.payment_provider || "—"}</Typography>
              <Typography variant="body2"><strong>Next billing date:</strong> {formatDate(detail.next_billing_date)}</Typography>
              <Typography variant="body2"><strong>Period start:</strong> {formatDate(current?.current_period_start)}</Typography>
              <Typography variant="body2"><strong>Period end:</strong> {formatDate(current?.current_period_end)}</Typography>
            </Stack>
            {access.notification?.message ? (
              <Alert severity="info" sx={{ mt: 2 }}>
                {access.notification.message}
              </Alert>
            ) : null}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Linked accounts
            </Typography>
            <Divider sx={{ mb: 2 }} />
            <Stack spacing={1}>
              <Typography variant="body2"><strong>Web student ID:</strong> {detail.linked_accounts?.web_student_id || "—"}</Typography>
              <Typography variant="body2"><strong>Parent ID:</strong> {detail.linked_accounts?.parent_id || "—"}</Typography>
              <Typography variant="body2"><strong>Parent email:</strong> {detail.linked_accounts?.parent_email || "—"}</Typography>
            </Stack>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Subscription history
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Provider</TableCell>
                  <TableCell>Period start</TableCell>
                  <TableCell>Period end</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detail.subscriptions || []).map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>{sub.id}</TableCell>
                    <TableCell>{sub.plan?.name || "—"}</TableCell>
                    <TableCell>
                      <Chip size="small" label={sub.status} color={statusColor(sub.status)} />
                    </TableCell>
                    <TableCell>{sub.payment_provider || "—"}</TableCell>
                    <TableCell>{formatDate(sub.current_period_start)}</TableCell>
                    <TableCell>{formatDate(sub.current_period_end)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Grid>

        <Grid item xs={12}>
          <Paper sx={{ p: 2 }}>
            <Typography variant="h6" gutterBottom>
              Payment history
            </Typography>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Method</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Amount</TableCell>
                  <TableCell>Plan</TableCell>
                  <TableCell>Reference</TableCell>
                  <TableCell>Receipt</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(detail.payment_history || []).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">No payments recorded yet.</TableCell>
                  </TableRow>
                ) : (
                  (detail.payment_history || []).map((payment, index) => (
                    <TableRow key={`${payment.id || "stripe"}-${payment.stripe_invoice_id || index}`}>
                      <TableCell>{formatDate(payment.paid_at)}</TableCell>
                      <TableCell>{payment.payment_method}</TableCell>
                      <TableCell>
                        <Chip size="small" label={payment.status} color={statusColor(payment.status)} />
                      </TableCell>
                      <TableCell>{formatMoney(payment.amount_pence, payment.currency)}</TableCell>
                      <TableCell>{payment.plan_name || "—"}</TableCell>
                      <TableCell>{payment.receipt_reference || "—"}</TableCell>
                      <TableCell>
                        {payment.receipt_url ? (
                          <Button size="small" href={payment.receipt_url} target="_blank" rel="noopener noreferrer">
                            View
                          </Button>
                        ) : (
                          "—"
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>

      <Dialog open={billingOpen} onClose={() => { setBillingOpen(false); resetBillingForm(); }} maxWidth="sm" fullWidth>
        <DialogTitle>Activate subscription / collect payment</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>Plan</InputLabel>
              <Select
                label="Plan"
                value={selectedPlanId}
                onChange={(e) => setSelectedPlanId(e.target.value)}
              >
                {plans.map((plan) => (
                  <MenuItem key={plan.id} value={String(plan.id)}>
                    {plan.name} — {formatMoney(plan.price_pence, plan.currency)} / {plan.billing_interval}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Divider />

            <Typography variant="subtitle2">Stripe payment link</Typography>
            <Typography variant="body2" color="text.secondary">
              Send the parent a browser checkout link (same flow as web portal payment links).
              {detail.linked_accounts?.parent_email
                ? ` Parent email: ${detail.linked_accounts.parent_email}`
                : ` Student email: ${user.email}`}
            </Typography>
            <FormControlLabel
              control={
                <Checkbox
                  checked={sendEmail}
                  onChange={(e) => setSendEmail(e.target.checked)}
                />
              }
              label="Email payment link to parent (or student if no parent on file)"
            />
            <Button
              variant="outlined"
              startIcon={<LinkIcon />}
              onClick={handleCreatePaymentLink}
              disabled={submittingLink}
            >
              {submittingLink ? "Creating…" : sendEmail ? "Create link & email parent" : "Create Stripe payment link"}
            </Button>
            {createdLinkUrl ? (
              <Alert severity="success">
                {createdLinkEmailInfo?.sent ? (
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Email sent to {createdLinkEmailInfo.email}.
                  </Typography>
                ) : null}
                {createdLinkEmailInfo?.error ? (
                  <Typography variant="body2" color="warning.main" sx={{ mb: 1 }}>
                    {createdLinkEmailInfo.error}
                  </Typography>
                ) : null}
                <Typography variant="body2" sx={{ wordBreak: "break-all" }}>
                  {createdLinkUrl}
                </Typography>
                <Button size="small" sx={{ mt: 1 }} onClick={() => navigator.clipboard.writeText(createdLinkUrl)}>
                  Copy link
                </Button>
              </Alert>
            ) : null}

            <Divider />

            <Typography variant="subtitle2">Bank transfer / offline payment</Typography>
            <Typography variant="body2" color="text.secondary">
              Upload the bank slip and activate the plan immediately (like web portal offline purchases).
            </Typography>
            <TextField
              label="Receipt reference"
              size="small"
              fullWidth
              value={receiptReference}
              onChange={(e) => setReceiptReference(e.target.value)}
            />
            <TextField
              label="Amount paid (£)"
              size="small"
              fullWidth
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
            <TextField
              label="Admin notes"
              size="small"
              fullWidth
              multiline
              minRows={2}
              value={adminNotes}
              onChange={(e) => setAdminNotes(e.target.value)}
            />
            <Button variant="outlined" component="label" startIcon={<ReceiptLongIcon />}>
              {bankReceiptFile ? bankReceiptFile.name : "Upload bank receipt"}
              <input
                ref={bankReceiptInputRef}
                hidden
                type="file"
                accept=".jpg,.jpeg,.png,.pdf"
                onChange={(e) => setBankReceiptFile(e.target.files?.[0] || null)}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setBillingOpen(false); resetBillingForm(); }}>Close</Button>
          <Button
            variant="contained"
            onClick={handleOfflinePayment}
            disabled={submittingOffline || !bankReceiptFile}
          >
            {submittingOffline ? "Saving…" : "Activate plan (offline)"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MobileUserDetails;
